// src/screens/PerfDashboard.jsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getPerfMonthDailyYoY,
  getPerfYearMonthlyYoY,
  getPerfSummaryMtd,
  getPerfPendingAging,
} from "../api/salesPerf";

import { THEME, appViewportStyle, screenStyle } from "../ui/theme";
import { Section, EmptyState } from "../ui/primitives";

import PerfMiniLines from "../layout/components/finance/PerfMiniLines";
import PerfYearGroupedBars from "../layout/components/finance/PerfYearGroupedBars";

const BR_TZ = "America/Sao_Paulo";
const BR_OFFSET_MIN = -180;

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function brYmdFromMs(ms) {
  const d = new Date(ms);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return { y: Number(map.year), m: Number(map.month), day: Number(map.day) };
}

function brYmFromMs(ms) {
  const d = new Date(ms);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TZ,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(d);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return { y: Number(map.year), m: Number(map.month) };
}

function brMidnightUtcMsFromYmd(y, m, day) {
  return Date.UTC(y, m - 1, day, 0, 0, 0, 0) - BR_OFFSET_MIN * 60 * 1000;
}

function startOfTomorrowBrUtcMs(nowMs) {
  const { y, m, day } = brYmdFromMs(nowMs);
  const t = Date.UTC(y, m - 1, day, 0, 0, 0, 0) + 24 * 60 * 60 * 1000;
  const d = new Date(t);
  return brMidnightUtcMsFromYmd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function monthWindowFromMonthStartUtcMs(monthStartUtcMs) {
  const { y, m } = brYmFromMs(monthStartUtcMs);
  const winStart = brMidnightUtcMsFromYmd(y, m, 1);
  const nextM = m === 12 ? 1 : m + 1;
  const nextY = m === 12 ? y + 1 : y;
  const winEnd = brMidnightUtcMsFromYmd(nextY, nextM, 1);
  return { start: winStart, end: winEnd };
}

function dayKeyBR(msUtc) {
  const d = new Date(msUtc);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * Preenche o mês inteiro (1 ponto por dia), alinhado ao calendário BR.
 * - CUR: null a partir de amanhã (quebra a linha)
 * - PY: carrega último valor conhecido até o fim do mês (linha tracejada completa)
 * - Margem: null quando CPV incompleto; também null no futuro
 */
function buildFilledDailySeries({ rawItems, windowStartMs, windowEndMs, cutFromMs }) {
  const map = new Map();
  for (const r of rawItems) {
    const ms = safeNum(r?.bucket_start_utc_ms);
    if (!Number.isFinite(ms)) continue;
    map.set(dayKeyBR(ms), r);
  }

  const out = [];
  const start = Number(windowStartMs);
  const end = Number(windowEndMs);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return out;

  const oneDay = 24 * 60 * 60 * 1000;
  let t = start;
  let lastPY = null;

  while (t < end) {
    const r = map.get(dayKeyBR(t));

    const isFuture = t >= cutFromMs;

    const curRaw = r ? safeNum(r.net_mtd_cur) : null;
    const pyRaw = r ? safeNum(r.net_mtd_py) : null;

    const py = pyRaw != null ? pyRaw : lastPY;
    if (pyRaw != null) lastPY = pyRaw;

    const marginRaw = r && r.margin_mtd_cur != null ? safeNum(r.margin_mtd_cur) : null;

    out.push({
      bucket_start_utc_ms: t,
      net_mtd_cur: isFuture ? null : curRaw,
      net_mtd_py: py,
      margin_mtd_cur: isFuture ? null : marginRaw,
    });

    t += oneDay;
  }

  return out;
}

function fmtMoneyBR(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(x, digits = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return (n * 100).toFixed(digits) + "%";
}

function deltaBadge(delta, theme) {
  const n = Number(delta);
  if (!Number.isFinite(n) || n === 0) return { txt: "0", color: theme.muted };
  const up = n > 0;
  return {
    txt: `${up ? "+" : ""}${fmtMoneyBR(n)}`,
    color: up ? "rgba(140,255,170,0.95)" : "rgba(255,140,140,0.95)",
  };
}

function cardStyle() {
  return {
    border: `1px solid ${THEME.border}`,
    borderRadius: 14,
    padding: 12,
    background: THEME.panel,
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
  };
}

function labelStyle() {
  return { fontSize: 11, letterSpacing: 0.4, color: THEME.muted, textTransform: "uppercase" };
}

function valueStyle() {
  return { fontSize: 18, fontWeight: 800, color: THEME.text, lineHeight: 1.15 };
}

function subStyle() {
  return { marginTop: 6, fontSize: 12, color: THEME.muted, display: "flex", gap: 10, flexWrap: "wrap" };
}

function barRow(label, v0, v1, v2) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: 10, alignItems: "center" }}>
      <div style={{ fontSize: 12, color: THEME.muted }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div style={{ ...cardStyle(), padding: "8px 10px", borderRadius: 12 }}>
          <div style={{ ...labelStyle(), fontSize: 10 }}>0–7d</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{v0}</div>
        </div>
        <div style={{ ...cardStyle(), padding: "8px 10px", borderRadius: 12 }}>
          <div style={{ ...labelStyle(), fontSize: 10 }}>8–30d</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{v1}</div>
        </div>
        <div style={{ ...cardStyle(), padding: "8px 10px", borderRadius: 12 }}>
          <div style={{ ...labelStyle(), fontSize: 10 }}>{">"}30d</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{v2}</div>
        </div>
      </div>
    </div>
  );
}

export default function PerfDashboard() {
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // === existing
  const qMonth = useQuery({
    queryKey: ["perf", "month_daily_yoy"],
    queryFn: () => getPerfMonthDailyYoY(),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const qYear = useQuery({
    queryKey: ["perf", "year_monthly_yoy"],
    queryFn: () => getPerfYearMonthlyYoY(),
    staleTime: 5 * 60_000,
    keepPreviousData: true,
  });

  // === NEW cards
  const qSummary = useQuery({
    queryKey: ["perf", "summary_mtd"],
    queryFn: () => getPerfSummaryMtd(),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const qPending = useQuery({
    queryKey: ["perf", "pending_aging"],
    queryFn: () => getPerfPendingAging(),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const monthWindow = useMemo(() => {
    const ms = safeNum(qMonth.data?.month_start_utc_ms);
    if (ms == null) return null;
    return monthWindowFromMonthStartUtcMs(ms);
  }, [qMonth.data]);

  const monthRawItems = useMemo(() => {
    const arr = qMonth.data?.items ?? [];
    return (Array.isArray(arr) ? arr : [])
      .map((r) => ({
        ...r,
        bucket_start_utc_ms: safeNum(r.bucket_start_utc_ms),
        net_mtd_cur: safeNum(r.net_mtd_cur),
        net_mtd_py: safeNum(r.net_mtd_py),
        margin_mtd_cur: r.margin_mtd_cur == null ? null : safeNum(r.margin_mtd_cur),
      }))
      .filter((r) => r.bucket_start_utc_ms != null)
      .sort((a, b) => a.bucket_start_utc_ms - b.bucket_start_utc_ms);
  }, [qMonth.data]);

  const monthItems = useMemo(() => {
    if (!monthWindow) return [];
    return buildFilledDailySeries({
      rawItems: monthRawItems,
      windowStartMs: monthWindow.start,
      windowEndMs: monthWindow.end,
      cutFromMs: startOfTomorrowBrUtcMs(nowTick ?? Date.now()),
    });
  }, [monthWindow, monthRawItems, nowTick]);

  const yearItems = useMemo(() => {
    const arr = qYear.data?.items ?? [];
    return (Array.isArray(arr) ? arr : [])
      .map((r) => ({
        ...r,
        month_index: safeNum(r.month_index),
        net_month_cur: safeNum(r.net_month_cur),
        net_month_py: safeNum(r.net_month_py),
      }))
      .filter((r) => r.month_index != null)
      .sort((a, b) => (a.month_index || 0) - (b.month_index || 0));
  }, [qYear.data]);

  const curMonthIndex = useMemo(() => new Date().getMonth() + 1, []);

  const mtd = qSummary.data?.mtd || null;
  const pending = qPending.data?.pending || null;

  const quality = qSummary.data?.quality || null;

  // derived for badges
  const netCur = safeNum(mtd?.net_cur) ?? null;
  const netPrevMtd = safeNum(mtd?.net_prev_mtd) ?? null;
  const netPy = safeNum(mtd?.net_py) ?? null;

  const dPrev = netCur != null && netPrevMtd != null ? netCur - netPrevMtd : null;
  const dYoY = netCur != null && netPy != null ? netCur - netPy : null;

  const dPrevB = deltaBadge(dPrev ?? NaN, THEME);
  const dYoYB = deltaBadge(dYoY ?? NaN, THEME);

  const cov = mtd?.cpv_coverage_pct;
  const marginPct = mtd?.margin_pct_basecpv;

  return (
    <div style={appViewportStyle()}>
      <div style={screenStyle()}>
        <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 12 }}>
          <Section
            title={<b style={{ fontSize: 14, letterSpacing: 0.4 }}>PERFORMANCE • FATURAMENTO</b>}
            subtitle="Topo da DRE: faturamento e margem bruta (margem só na base CPV)."
          />

          {/* === NEW: Resumo MTD (cards) */}
          <Section
            title="Resumo MTD"
            subtitle={
              <span>
                Cards executivos…{" "}
                <span style={{ color: THEME.muted }}>
                  • Última ingestão: {quality?.last_ingest_at ? new Date(quality.last_ingest_at).toLocaleString("pt-BR") : "—"}
                  {"  "}• Últ. CPV: {quality?.last_cpv_update_at ? new Date(quality.last_cpv_update_at).toLocaleString("pt-BR") : "—"}
                </span>
              </span>
            }
          >
            {qSummary.isLoading && !qSummary.data ? (
              <div style={{ color: THEME.muted, padding: 12 }}>Carregando resumo…</div>
            ) : qSummary.isError ? (
              <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>Falha ao carregar resumo MTD</div>
            ) : !mtd ? (
              <EmptyState>Sem dados de resumo.</EmptyState>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                  gap: 10,
                }}
              >
                {/* Card 1 */}
                <div style={cardStyle()}>
                  <div style={labelStyle()}>Receita líquida (MTD)</div>
                  <div style={valueStyle()}>R$ {fmtMoneyBR(mtd.net_cur)}</div>
                  <div style={subStyle()}>
                    <span style={{ color: dPrevB.color }}>Δ vs MTD anterior: {dPrevB.txt}</span>
                    <span style={{ color: dYoYB.color }}>Δ YoY: {dYoYB.txt}</span>
                  </div>
                </div>

                {/* Card 2 */}
                <div style={cardStyle()}>
                  <div style={labelStyle()}>Margem bruta (R$) • base CPV</div>
                  <div style={valueStyle()}>R$ {fmtMoneyBR(mtd.margin_cur_basecpv)}</div>
                  <div style={subStyle()}>
                    <span>Receita base CPV: R$ {fmtMoneyBR(mtd.revenue_basecpv)}</span>
                  </div>
                </div>

                {/* Card 3 */}
                <div style={cardStyle()}>
                  <div style={labelStyle()}>Margem bruta % • base CPV</div>
                  <div style={valueStyle()}>{marginPct == null ? "—" : fmtPct(marginPct, 1)}</div>
                  <div style={subStyle()}>
                    <span>CPV coverage: {cov == null ? "—" : fmtPct(cov, 0)}</span>
                  </div>
                </div>

                {/* Card 4 */}
                <div style={cardStyle()}>
                  <div style={labelStyle()}>Pendências (abertas)</div>
                  <div style={valueStyle()}>
                    {(pending?.cpv?.total_items ?? 0) + (pending?.embarque?.total_nf ?? 0)}
                  </div>
                  <div style={subStyle()}>
                    <span>Itens sem CPV: {pending?.cpv?.total_items ?? 0}</span>
                    <span>NFs sem embarque: {pending?.embarque?.total_nf ?? 0}</span>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* === NEW: Pendências aging (fila de trabalho) */}
          <Section
            title="Pendências (aging)"
            subtitle="Fila de trabalho por idade (dias desde emissão)."
          >
            {qPending.isLoading && !qPending.data ? (
              <div style={{ color: THEME.muted, padding: 12 }}>Carregando pendências…</div>
            ) : qPending.isError ? (
              <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
                Falha ao carregar pendências
              </div>
            ) : !pending ? (
              <EmptyState>Sem dados de pendências.</EmptyState>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {barRow(
                  "Sem CPV",
                  pending.cpv?.b0_7 ?? 0,
                  pending.cpv?.b8_30 ?? 0,
                  pending.cpv?.b30p ?? 0
                )}
                {barRow(
                  "Sem embarque",
                  pending.embarque?.b0_7 ?? 0,
                  pending.embarque?.b8_30 ?? 0,
                  pending.embarque?.b30p ?? 0
                )}
              </div>
            )}
          </Section>

          {/* === existing month chart (kept) */}
          <Section
            title="Evolução no mês (Janela do mês inteira)"
            subtitle="Fat líquido acumulado (MTD) • Cur (até hoje) vs Ano anterior (até final do mês, tracejado) • Margem bruta (MTD) corta quando CPV incompleto"
          >
            {qMonth.isLoading && !qMonth.data ? (
              <div style={{ color: THEME.muted, padding: 12 }}>Carregando série do mês…</div>
            ) : qMonth.isError ? (
              <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
                Falha ao carregar série do mês
              </div>
            ) : !monthItems.length ? (
              <EmptyState>Sem pontos no mês corrente.</EmptyState>
            ) : (
              <div style={{ height: 240 }}>
                <PerfMiniLines
                  theme={THEME}
                  height={240}
                  items={monthItems}
                  xKey="bucket_start_utc_ms"
                  windowStartMs={monthWindow?.start ?? null}
                  windowEndMs={monthWindow?.end ?? null}
                  nowTick={nowTick}
                  showTodayLine={false}
                  showNowLine={true}
                  series={[
                    {
                      key: "net_mtd_cur",
                      name: "Fat líquido (MTD)",
                      stroke: THEME.chartPrimary,
                      cutAfterToday: true,
                      connectNulls: false,
                    },
                    {
                      key: "net_mtd_py",
                      name: "Fat líquido (MTD) • ano anterior",
                      stroke: THEME.chartSecondary,
                      dashed: true,
                      cutAfterToday: false,
                      connectNulls: true,
                    },
                    {
                      key: "margin_mtd_cur",
                      name: "Margem bruta (MTD) • base CPV",
                      stroke: "rgba(180,255,180,0.92)",
                      cutAfterToday: true,
                      connectNulls: false,
                    },
                  ]}
                />
              </div>
            )}
          </Section>

          {/* === existing year chart (kept) */}
          <Section
            title="Faturamento mensal no ano (12 meses)"
            subtitle="Fat líquido por mês • PY aparece no ano todo • Cur aparece até o mês atual (mês atual pode estar em aberto)"
          >
            {qYear.isLoading && !qYear.data ? (
              <div style={{ color: THEME.muted, padding: 12 }}>Carregando série anual…</div>
            ) : qYear.isError ? (
              <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
                Falha ao carregar série anual
              </div>
            ) : !yearItems.length ? (
              <EmptyState>Sem pontos no ano corrente.</EmptyState>
            ) : (
              <div style={{ height: 240 }}>
                <PerfYearGroupedBars theme={THEME} height={240} items={yearItems} curMonthIndex={curMonthIndex} />
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
